import { useEffect, useState } from "react";
import axios from "axios";
import { GitPullRequest, Check, X } from "lucide-react";
import { API, STATUS_META, PROP_STATUS, PROP_TYPES, formatError } from "./constants";

const FIELD_LABELS = {
  status: "Status",
  notes: "Notes",
  follow_up: "Follow-up",
  assigned_to: "Assigned to",
  assigned_name: "Assigned to",
  client_id: "Client",
  client_name: "Client",
  name: "Name",
  type: "Type",
  city: "City",
  location: "Location",
  size_sqft: "Size (sq.ft)",
  price: "Price",
};

const prettyValue = (k, v) => {
  if (v === "" || v == null) return "—";
  if (k === "status" && STATUS_META[v]) return STATUS_META[v].label;
  if (k === "status" && PROP_STATUS[v]) return PROP_STATUS[v].label;
  if (k === "type" && PROP_TYPES[v]) return PROP_TYPES[v];
  return String(v);
};

export const RequestsView = ({ user }) => {
  const [requests, setRequests] = useState(null);
  const [error, setError] = useState("");

  const fetchRequests = () => {
    axios
      .get(`${API}/change-requests`, { withCredentials: true })
      .then((res) => setRequests(res.data))
      .catch(() => setRequests([]));
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const resolve = async (id, action) => {
    setError("");
    try {
      await axios.post(`${API}/change-requests/${id}/${action}`, {}, { withCredentials: true });
      fetchRequests();
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    }
  };

  const pending = (requests || []).filter((r) => r.status === "pending");
  const resolved = (requests || []).filter((r) => r.status !== "pending");

  return (
    <div className="mt-6" data-testid="requests-view">
      <div className="rounded-xl border border-border bg-white p-6 shadow-soft">
        <div className="flex items-center gap-2">
          <GitPullRequest size={16} className="text-amberdark" />
          <h2 className="font-display text-lg font-extrabold tracking-tight text-navy">Change Requests</h2>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          When a co-founder edits something another co-founder last changed, it lands here for approval instead of applying directly.
        </p>

        {error && <p className="mt-4 text-sm font-semibold text-red-600" data-testid="requests-error">{error}</p>}

        {requests === null ? (
          <p className="py-8 text-xs font-bold uppercase tracking-[0.2em] text-gray-500" data-testid="requests-loading">Loading...</p>
        ) : pending.length === 0 ? (
          <p className="py-8 text-xs font-bold uppercase tracking-[0.2em] text-gray-500" data-testid="requests-empty">No pending requests. All clear.</p>
        ) : (
          <div className="mt-6 space-y-4" data-testid="requests-list">
            {pending.map((r, i) => {
              const own = r.requested_by === user.id;
              return (
                <div key={r.id} className="rounded-xl border border-amber/50 bg-[#FFFBF2] p-5" data-testid={`request-card-${i}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-bold text-navy">
                        {r.requested_by_name}
                        <span className="mx-2 text-gray-400">→</span>
                        {r.entity_type === "leads" ? "Lead" : "Property"}: {r.entity_name}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {r.created_at ? new Date(r.created_at).toLocaleString("en-IN") : ""}
                      </p>
                    </div>
                    {own ? (
                      <span className="text-xs font-semibold text-gray-500">Waiting for another co-founder</span>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={() => resolve(r.id, "accept")}
                          data-testid={`request-accept-${i}`}
                          className="flex items-center gap-1.5 rounded-md bg-green-600 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-green-700"
                        >
                          <Check size={13} />
                          Accept
                        </button>
                        <button
                          onClick={() => resolve(r.id, "reject")}
                          data-testid={`request-reject-${i}`}
                          className="flex items-center gap-1.5 rounded-md border-2 border-red-300 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-red-600 transition-colors duration-200 hover:bg-red-50"
                        >
                          <X size={13} />
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {Object.entries(r.changes || {})
                      .filter(([k]) => !k.startsWith("last_") && k !== "updated_at")
                      .map(([k, v]) => (
                        <span key={k} className="rounded-md border border-border bg-white px-3 py-1.5 text-xs text-gray-700">
                          <span className="font-bold text-navy">{FIELD_LABELS[k] || k}:</span> {prettyValue(k, v)}
                        </span>
                      ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {resolved.length > 0 && (
          <div className="mt-8 border-t border-border pt-5" data-testid="requests-history">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">Recent history</p>
            <ul className="mt-3 space-y-2">
              {resolved.slice(0, 10).map((r) => (
                <li key={r.id} className="flex items-center gap-3 text-xs text-gray-500">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${r.status === "accepted" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                    {r.status}
                  </span>
                  <span className="font-semibold text-navy">{r.entity_name}</span>
                  <span>by {r.requested_by_name}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
